import { useMsal } from "@azure/msal-react";
import React, { useEffect } from "react";
import styled from "styled-components";

import { CoeLogo, Typography, GoogleLoginButton, MSLoginButton, Alert } from "../components";
import { COLORS } from "../constants";
import { publicClientAppScopes, submitLoginGoogleOAuthForm } from "../utils";

import { LOGIN_INTENT, AUTH_SEARCH_PARAMS_KEY, AUTH_REDIRECT_URL_KEY } from "./AuthGuard";

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 32px;

  background-image: url("../static/login-gradient-bg.png");
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;

  text-align: center;

  a {
    font-size: 14px;
    font-weight: 600;
    color: ${COLORS.black85};
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  text-align: center;
  margin-bottom: 24px;
`;

const LoginOptions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  text-align: center;
`;

const StyledLogo = styled(CoeLogo)`
  width: 240px;
  margin-bottom: 24px;
`;

const defaultIndent = {
  message: "Welcome to Coefficient!",
  description: "Sign in with your Google or Microsoft account to get started.",
};

export const Login: React.FC = () => {
  const { instance: publicClientApp } = useMsal();
  const [intent, setIntent] = React.useState<{
    message: string;
    description: string;
  }>();

  useEffect(() => {
    try {
      const loginIntent = sessionStorage.getItem(LOGIN_INTENT) || "";
      if (!loginIntent) {
        return;
      }
      const alertMessage = JSON.parse(decodeURIComponent(loginIntent));
      setIntent({
        ...defaultIndent,
        ...alertMessage,
      });
    } catch (error) {
      // skip
    }
  }, []);

  // The server sets a short-lived `oauth_login_error` cookie when Google sign-in
  // fails (account chooser dismissed, or the chosen account has no Coefficient user).
  // A cookie — not a query param — because AuthGuard's unauthenticated redirect does
  // history.push("/") and strips the query string before this page reads it. Read it
  // once and clear it so the alert shows exactly once.
  const [hasOAuthError, setHasOAuthError] = React.useState(false);
  useEffect(() => {
    const hasErrorCookie = document.cookie.split("; ").some(c => c === "oauth_login_error=1");
    if (hasErrorCookie) {
      setHasOAuthError(true);
      document.cookie = "oauth_login_error=; Max-Age=0; Path=/";
    }
  }, []);

  // Persist the query string for post-login restoration (defensively dropping any
  // transient `oauth_error` flag so it can never ride along to the destination URL).
  const storeSearchParamsForRestore = () => {
    const params = new URLSearchParams(location.search);
    params.delete("oauth_error");
    const cleaned = params.toString();
    if (cleaned) {
      localStorage.setItem(AUTH_SEARCH_PARAMS_KEY, `?${cleaned}`);
    } else {
      localStorage.removeItem(AUTH_SEARCH_PARAMS_KEY);
    }
  };

  const handleGoogleLoginClick = () => {
    // Store search parameters in localStorage before OAuth redirect
    storeSearchParamsForRestore();

    // Get redirect URL from sessionStorage (set by AuthGuard when redirecting to login)
    const redirectUrl = sessionStorage.getItem(AUTH_REDIRECT_URL_KEY) || undefined;

    // Standard OAuth2 code flow with prompt=select_account. Unlike the previous GIS
    // credential flow, this always shows an account chooser, so users signed into
    // multiple Google accounts no longer get stuck in a login loop.
    submitLoginGoogleOAuthForm({ redirectUrl });
  };

  const handleMSLoginClick = () => {
    // Store search parameters in localStorage before OAuth redirect
    storeSearchParamsForRestore();

    publicClientApp.loginRedirect({
      scopes: publicClientAppScopes,
      // Force the account chooser so users signed into multiple Microsoft accounts
      // pick the right one, matching the Google sign-in behavior.
      prompt: "select_account",
    });
  };

  return (
    <Container>
      <div>
        <StyledLogo id="coeff__logo" />
        {hasOAuthError && (
          <Alert
            type="error"
            showIcon
            message="Sign-in failed"
            description="We couldn't sign you in. If you're signed into multiple Google accounts, choose the one for your Coefficient account and try again."
          />
        )}
        {!!intent && <Alert type="info" {...intent} />}
      </div>

      <Content>
        <Typography variant="h1" fontWeight={800}>
          Sign in
        </Typography>

        <LoginOptions>
          <>
            <div>
              <Typography gutterBottom>Using your Google Account</Typography>
              <GoogleLoginButton onClick={handleGoogleLoginClick} />
            </div>

            <>
              <Typography variant="body2" fontWeight={600}>
                OR
              </Typography>

              <div>
                <Typography gutterBottom>Using your Microsoft Account</Typography>
                <MSLoginButton onClick={() => handleMSLoginClick()} />
              </div>
            </>
          </>
        </LoginOptions>
      </Content>

      <div>
        <Typography variant="body3" fontWeight={500}>
          Having trouble accessing your account?
        </Typography>

        <a href="mailto:support@coefficient.io">Contact Support</a>
      </div>
    </Container>
  );
};
